/**
 * Scheduled ticket modal — shown when the user clicks a recurring-scheduled
 * activity in the home page "Scheduled activities" list.
 *
 * Rendered as a MUI Popover anchored below the clicked activity row.
 * Close: outside click (MUI built-in) or the X button.
 *
 * Actions:
 *  - Skip         → `useBulkSkipNextCycleRecurringTickets`
 *  - Generate now → `useCreateRecurringTicketNow`
 * Both show loading state on the button, toast on success/error, and close
 * the popover only on success. On error the popover stays open so the user
 * can retry.
 */
import { Stack, Typography, useTheme } from "@mui/material";
import { Button, ClockIcon, CloseIcon, InfoIcon } from "components";
import {
  useCreateRecurringTicketNow,
  useGlobalStore,
  useSkipRecurringTicketDates,
} from "controller";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { addMixpanelEvent, convertUTCtoTimezoneV2 } from "utilz/helpers";
import { stripHtml } from "utilz/helpers/stripHtml";

export type ScheduledTicketAssignee = {
  first_name?: string;
  last_name?: string;
  profile_image?: string | null;
  assignee_first_name?: string;
  assignee_last_name?: string;
  assignee_profile_image?: string | null;
  user_first_name?: string;
  user_last_name?: string;
  user_profile_image?: string | null;
};

interface ScheduledTicketModalProps {
  /** Caller is responsible for closing the popover (e.g. on success). */
  onClose: () => void;
  recurringTicketId: string;
  title: string;
  /** Raw ISO start_time from the upcoming-activities API. */
  scheduledAtIso: string;
  /** Assignees as returned by the upcoming-activities API. */
  assignees?: ScheduledTicketAssignee[];
  /** Ticket description as returned by the upcoming-activities API. */
  description?: string | null;
}

export const ScheduledTicketModal = ({
  onClose,
  recurringTicketId,
  title,
  scheduledAtIso,
  description,
}: ScheduledTicketModalProps) => {
  const theme = useTheme();
  // Display in the resource's own timezone — company timezone is config-only.
  const resourceTimezone = useGlobalStore((state) => state.timezone);
  const userDateFormat =
    useGlobalStore((state) => state.dateFormat) || "MM/DD/YYYY";

  const isPastSchedule = scheduledAtIso
    ? dayjs
        .utc(scheduledAtIso)
        .tz(resourceTimezone)
        .isBefore(dayjs().tz(resourceTimezone))
    : false;

  const { mutateAsync: skipDates, isLoading: isSkipping } =
    useSkipRecurringTicketDates();
  const { mutateAsync: createNow, isLoading: isGenerating } =
    useCreateRecurringTicketNow();

  const handleSkip = async () => {
    try {
      await skipDates({
        recurringTicketId,
        payload: {
          skip_dates: [{ skip_run_time: scheduledAtIso }],
        },
      });
      addMixpanelEvent({ event: "recurring_ticket_scheduled_date_skipped" });
      toast.success("Scheduled ticket skipped successfully");
      onClose();
    } catch (error) {
      const message =
        (error as { message?: string } | undefined)?.message ||
        "Failed to skip recurring ticket.";
      toast.error(message);
    }
  };

  const handleGenerateNow = async () => {
    addMixpanelEvent({ event: "recurring_ticket_create_now_clicked" });
    try {
      await createNow({ mappingId: recurringTicketId });
      toast.success("Recurring ticket generated successfully");
      onClose();
    } catch (error) {
      const message =
        (error as { message?: string } | undefined)?.message ||
        "Recurring ticket generation failed.";
      toast.error(message);
    }
  };

  const scheduledLabel = (() => {
    if (!scheduledAtIso) return "";
    const d = convertUTCtoTimezoneV2(scheduledAtIso, resourceTimezone);
    return d?.isValid() ? d.format(`${userDateFormat} hh:mm A`) : "";
  })();

  return (
    <Stack
      sx={{
        width: "340px",
        p: "12px",
        gap: "12px",
        bgcolor: theme.palette.neutral.main,
        borderRadius: "12px",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="4px"
      >
        <Typography
          variant="caption-2"
          sx={{
            color: theme.palette.lightBg.high,
            flex: 1,
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          {title && title.length > 100 ? `${title.slice(0, 100)}...` : title}
        </Typography>
        <CloseIcon
          aria-label="Close"
          onClick={onClose}
          sx={{
            color: theme.palette.lightBg.medium,
            cursor: "pointer",
            width: "16px",
            height: "16px",
            flexShrink: 0,
          }}
        />
      </Stack>

      {/* Info banner */}
      <Stack
        direction="row"
        gap="8px"
        alignItems="flex-start"
        sx={{
          p: "12px",
          borderRadius: "8px",
          backgroundColor: theme.palette.info.tint,
          border: `1px solid ${theme.palette.info.main}`,
        }}
      >
        <InfoIcon
          sx={{
            color: theme.palette.info.main,
            width: "16px",
            height: "16px",
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body-xsmall"
          sx={{ color: theme.palette.lightBg.high, width: "90%" }}
        >
          This ticket hasn&apos;t been created yet. It&apos;ll be generated on{" "}
          {scheduledLabel} and assigned a ticket ID then.
        </Typography>
      </Stack>

      {/* Details */}
      <Stack gap="16px">
        <Stack direction="row" alignItems="center">
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{
              width: "140px",
              color: theme.palette.lightBg.low,
            }}
          >
            <ClockIcon sx={{ width: "20px", height: "20px" }} />
            <Typography variant="body-small" color={theme.palette.lightBg.low}>
              Scheduled
            </Typography>
          </Stack>
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.high }}
          >
            {scheduledLabel}
          </Typography>
        </Stack>

        {/*
          TODO: re-enable once backend ships assignees on the upcoming-activities
          response. Awaiting the shape (`first_name` vs `assignee_first_name`
          vs `user_first_name`) — unhide when confirmed.

          <Stack direction="row" alignItems="center">
            <Stack
              direction="row"
              alignItems="center"
              gap="8px"
              sx={{
                width: "140px",
                color: theme.palette.lightBg.low,
              }}
            >
              <UserCircleIcon sx={{ width: "20px", height: "20px" }} />
              <Typography
                variant="body-small"
                color={theme.palette.lightBg.low}
              >
                {isSingleAssignee ? "Assignee" : "Assignees"}
              </Typography>
            </Stack>
            {assignees.length === 0 ? (
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.lightBg.medium }}
              >
                —
              </Typography>
            ) : isSingleAssignee ? (
              <Stack direction="row" alignItems="center" gap="8px">
                <Avatar
                  fullname={assignees[0].fullName}
                  url={assignees[0].profileImage}
                  sx={{ width: "24px", height: "24px", fontSize: "10px" }}
                />
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.lightBg.high }}
                >
                  {assignees[0].fullName}
                </Typography>
              </Stack>
            ) : (
              <AvatarGroup max={3} childWidthHeight="24px">
                {assignees.map((a, i) => (
                  <Avatar key={i} fullname={a.fullName} url={a.profileImage} />
                ))}
              </AvatarGroup>
            )}
          </Stack>
          */}
      </Stack>

      <Stack
        sx={{
          p: "8px",
          borderRadius: "8px",
          backgroundColor: theme.palette.neutral["005"],
          gap: "8px",
        }}
      >
        <Typography
          variant="caption-3"
          sx={{ color: theme.palette.lightBg.high, fontWeight: 600 }}
        >
          Description
        </Typography>
        <Typography
          variant="body-xsmall"
          sx={{
            color: theme.palette.lightBg.low,
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          {(() => {
            // Backend returns rich-text HTML (<p>, <br>, etc.) — strip
            // tags before truncating so we don't count markup chars or
            // cut in the middle of a tag.
            const desc = stripHtml(description ?? "");
            if (!desc) return "—";
            return desc.length > 325 ? `${desc.slice(0, 325)}...` : desc;
          })()}
        </Typography>
      </Stack>

      {/* Footer */}
      <Stack direction="row" gap="8px">
        <Button
          variant="secondary"
          onClick={() => {
            void handleSkip();
          }}
          loading={isSkipping}
          disabled={isPastSchedule || isSkipping || isGenerating}
          sx={{ flex: 1 }}
        >
          Skip
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            void handleGenerateNow();
          }}
          loading={isGenerating}
          disabled={isSkipping || isGenerating}
          sx={{ flex: 1 }}
        >
          Generate now
        </Button>
      </Stack>
    </Stack>
  );
};
